"use client";

import React, { useRef } from "react";
import { CedeHubLogo as CedeHubLogoL } from "./CedeHubLogo-L";
import { CedeHubLogo as CedeHubLogoM } from "./CedeHubLogo-M";
import { ROUTES } from "@/constants/routes";
import { useDevice } from "@/hooks/ui/useDevice";
import { cn } from "@/lib/utils";
import { useRouter } from "next/navigation";

const LogoWrapper = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  const router = useRouter();
  const holdTimerRef = useRef<NodeJS.Timeout | null>(null);
  const holdCompletedRef = useRef<boolean>(false);

  const handleMouseDown = () => {
    // Reset the hold completed flag
    holdCompletedRef.current = false;
    // Start a timer for 3 seconds
    holdTimerRef.current = setTimeout(() => {
      holdCompletedRef.current = true;
      holdTimerRef.current = null;
      router.push(ROUTES.CLICK_AND_EARN);
    }, 3000);
  };

  const handleMouseUp = () => {
    // Clear the timer if mouse is released before 3 seconds
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const handleMouseLeave = () => {
    // Clear the timer if mouse leaves the logo
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
  };

  const handleClick = () => {
    // Clear the timer as a safety measure to prevent double navigation
    if (holdTimerRef.current) {
      clearTimeout(holdTimerRef.current);
      holdTimerRef.current = null;
    }
    // Prevent navigation if the hold gesture was completed
    if (holdCompletedRef.current) {
      holdCompletedRef.current = false;
      return;
    }
    // Navigate to home on normal click
    router.push(ROUTES.HOME);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Go to home"
      className={cn("flex w-fit cursor-pointer", className)}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {children}
    </div>
  );
};

export const CedeHubLogo = () => {
  const { isDesktop, isMounted } = useDevice();

  // Render consistent initial state to avoid hydration mismatch
  if (!isMounted) {
    return (
      <LogoWrapper>
        <CedeHubLogoL />
      </LogoWrapper>
    );
  }

  // After mount, render based on screen size
  return <LogoWrapper>{isDesktop ? <CedeHubLogoL /> : <CedeHubLogoM />}</LogoWrapper>;
};
